import type {
    IOffice,
    TOfficeDeliveryType,
    ICourier,
    ITimeSlotsResponse,
    IDeliveriesSearchResponse,
    TDeliveryType,
    TOfficeOption,
    TDeliveryFinishArgs,
    TDeliveryStatus,
    ITimeSlots,
} from '../models';

export const filterTimeSlots = (
    availableDates: ITimeSlots['availableDates'],
    timeSlotDaysCount: number
): ITimeSlots['availableDates'] => {
    return availableDates.slice(0, timeSlotDaysCount);
};

export const getOfficeDeliveryType = (offices?: IOffice[]): TOfficeDeliveryType =>
    offices?.[0]?.attributes?.anyOfficeUnnamedEnabled ? 'any' : 'specific';

const isDeliveryTypesAvailable = (deliveryType: IOffice | ICourier) => {
    return Object.values(deliveryType.attributes).some(attr => attr);
};

export const getIsCourierDeliveryAvailable = (courier?: ICourier) => {
    if (!courier) {
        return false;
    }

    return isDeliveryTypesAvailable(courier);
};

export const getAvailableOffices = (offices?: IOffice[]) => {
    if (!offices) {
        return [];
    }

    return offices.filter(office => isDeliveryTypesAvailable(office));
};

type TGetAvailableDelivery = (obj: {
    courier?: ICourier;
    offices?: IOffice[];
    onlyCourierDelivery?: boolean;
    timeSlots?: ITimeSlotsResponse['availableDates'];
}) => {
    offices?: IDeliveriesSearchResponse['offices'];
    timeSlots?: ITimeSlotsResponse['availableDates'];
} | null;

export const getAvailableDelivery: TGetAvailableDelivery = ({
    courier,
    offices,
    onlyCourierDelivery,
    timeSlots,
}) => {
    const isCourierDeliveryAvailable =
        getIsCourierDeliveryAvailable(courier) && timeSlots && timeSlots.length > 0;
    const availableOffices = getAvailableOffices(offices);
    const isOfficesAvailable = availableOffices?.length > 0;

    // Если onlyCourierDelivery=true
    if (onlyCourierDelivery) {
        // Если курьерка доступна - предлагаем только курьерку
        if (isCourierDeliveryAvailable) {
            return {
                timeSlots,
            };
        }

        // Если курьерка недоступна, но доступны офисы - предлагаем только офисы
        if (isOfficesAvailable) {
            return {
                offices: availableOffices,
            };
        }
    }

    // Если доступна курьерка и офисы и onlyCourierDelivery=false
    if (isCourierDeliveryAvailable && isOfficesAvailable) {
        return {
            offices: availableOffices,
            timeSlots,
        };
    }

    // Если доступна курьерка - предлагаем только курьерку
    if (isCourierDeliveryAvailable) {
        return {
            timeSlots,
        };
    }

    // Если доступны офисы - предлагаем только офисы
    if (isOfficesAvailable) {
        return {
            offices: availableOffices,
        };
    }

    // Если нет доступных вариантов доставки !isCourierDeliveryAvailable && !isOfficesAvailable
    return null;
};

export const getDeliveryAddress = (
    deliveryType: TDeliveryType,
    clientAddress?: string,
    office?: TOfficeOption
) => {
    if (deliveryType === 'courier' && clientAddress) {
        return clientAddress;
    }

    if (office) {
        const { category, content } = office;

        return `г ${category}, ${content}`;
    }
};

export const getDeliveryTypeStatus = (data: TDeliveryFinishArgs): TDeliveryStatus => {
    if ('courierDelivery' in data) {
        // TODO: было бы хорошо заменить на константы
        return 'COURIER_DELIVERY';
    }

    if (!data.isUnnamedCard) {
        return 'NAMED_OFFICE';
    }

    if (!('deliveryUuid' in data)) {
        return 'ANY_OFFICE';
    }

    return 'UNNAMED_OFFICE';
};
